import { DatePipe, formatDate } from '@angular/common';
import { Component, Inject, ViewChild } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatTable } from '@angular/material/table';
import { ToastrService } from 'ngx-toastr';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { DateTimePickerComponent } from '../date-time-picker/date-time-picker.component';
import { LookupDataService } from '../shared/lookup-data.service';
import { LookupItem } from '../shared/lookup-item.model';
import { UnloadingSpecies } from '../shared/unloading-species.model';
import { Unloading } from '../shared/unloading.model';
import { ConfirmDialogComponent } from '../vessel-reg/confirm-dialog/confirm-dialog.component';
import { AddUnloadingSpeciesDialogComponent } from './add-unloading-species-dialog/add-unloading-species-dialog.component';


@Component({
    selector: 'app-add-unloading-dialog',
    templateUrl: './add-unloading-dialog.component.html',
    styleUrl: './add-unloading-dialog.component.css',
    providers: [DatePipe],
    standalone: false
})
export class AddUnloadingDialogComponent {

  portOptions: LookupItem[] = [];
  filteredPorts!: Observable<LookupItem[]>;
  isSelectingOption = false;

  unloadingSpeciesColumns: string[] = ['species', 'poundsLanded', 'landingReceipt'];
  @ViewChild(MatTable) table!: MatTable<UnloadingSpecies>

  unloadingSpecies: UnloadingSpecies[] = [];
  unloadingForm: FormGroup;


  constructor(
    public dialog: MatDialog, 
    public dialogRef: MatDialogRef<AddUnloadingDialogComponent>,
    private lookupService: LookupDataService,
    private datePipe: DatePipe,
    private toastr: ToastrService,
    @Inject(MAT_DIALOG_DATA) public data: { unloading: Unloading, isNew: boolean, minDate: Date | null })
  {
      this.unloadingForm = new FormGroup({
        unloadingPort: new FormControl<LookupItem>(new LookupItem(''), [Validators.required]),
        unloadingDate: new FormControl('', [
          Validators.required,
          this.minDateValidator(data.minDate),
        ]),
      });
  }

  ngOnInit(): void {

    const formattedDate = this.data.unloading.unloadingDate ? this.datePipe.transform(this.data.unloading.unloadingDate, 'EEEE, MMMM d, y') : '';
    let port: LookupItem = LookupItem.transformAnonymous(this.data.unloading.unloadingPort);

    this.unloadingForm.patchValue({
      unloadingPort: port,
      unloadingDate: formattedDate,
    });
    this.data.unloading.species.forEach((species, index) => {
      this.unloadingSpecies[index] = species;
    });

    this.portOptions = this.lookupService.getHmsPorts();
    this.filteredPorts = this.unloadingForm.get('unloadingPort')!.valueChanges.pipe(
      startWith(port),
      map(value => this.portOptions.filter(option => option.name.toLowerCase().includes(value.toLowerCase())
      )) 
    );

  }

  closeDialog() {
    this.dialogRef.close();
  }

  addUnloading() {
    if (this.unloadingForm.valid) {
      let newUnloading: Unloading = new Unloading();
      newUnloading.unloadingPort = this.unloadingForm.get('unloadingPort')?.value;
      newUnloading.unloadingDate = this.unloadingForm.get('unloadingDate')?.value;
      newUnloading.species = this.unloadingSpecies;
      this.dialogRef.close(newUnloading);
    } else {
      this.unloadingForm.markAllAsTouched();
      this.toastr.error('Please correct the highlighted fields', 'Unable to save unloading');
    }
  }


  addSpecies(index: number) {
    let isNew: boolean = false;
    if (index == this.unloadingSpecies.length) {
      this.unloadingSpecies.push(new UnloadingSpecies());
      isNew = true;
    }
    const dialogRef = this.dialog.open(AddUnloadingSpeciesDialogComponent, {
      width: '425px',
      height: '440px',
      data: { species: this.unloadingSpecies[index] }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        if (result.delete) {
          this.unloadingSpecies.splice(index, 1);
        } else {
          this.unloadingSpecies[index].speciesName = result.speciesName;
          this.unloadingSpecies[index].poundsLanded = result.poundsLanded;
          this.unloadingSpecies[index].landingReceipt = result.landingReceipt;
        }
        this.table.renderRows();
      } else if (isNew) this.unloadingSpecies.splice(index, 1)  //if the dialog is cancelled, need to delete the element we pushed
      
    });
  }

  openDateDialog() {
    let dateInput = this.unloadingForm.get('unloadingDate')?.value == '' ? new Date() : new Date(this.unloadingForm.get('unloadingDate')?.value);

    const dialogRef = this.dialog.open(DateTimePickerComponent, {
      width: '500px',
      data: { date: dateInput, minDate: this.data.minDate, isUnloading: true },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) this.unloadingForm.get('unloadingDate')?.setValue(formatDate(result, 'fullDate', 'en-US'));
    });
  }

  displayFn(port: any): string {
    return port ? port.name : '';
  }


  deleteUnloading() {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      autoFocus: false,
      data: { screen: 'deleteunloading' },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.dialogRef.close({ delete: true });
      }
    });
  }

  clearUnloadingPort() {
    if (this.isSelectingOption) {
      this.isSelectingOption = false; // Reset flag after selection
      return; // Skip clearing if an option is being selected
    }
    this.unloadingForm.get('unloadingPort')?.setValue('');
    this.filteredPorts = this.unloadingForm.get('unloadingPort')!.valueChanges.pipe(
      startWith(''),
      map(() => this.portOptions) // Show all options when the field is empty
    );
  }

  // Modify this method to set the flag when an option is selected
  onOptionSelected() {
    this.isSelectingOption = true;
  }

  minDateValidator = (minDate: Date | null): ValidatorFn => {
    return (control: AbstractControl): ValidationErrors | null => {
      // No return date entered yet — nothing to validate against
      if (!minDate || isNaN(minDate.getTime())) {
        return null;
      }
      const controlDate = new Date(control.value);
      // If the control is empty or the date is invalid, return null (no error)
      if (!control.value || isNaN(controlDate.getTime())) {
        return null;
      }
      // Compare at day granularity so a same-day unloading is valid regardless of time
      const min = new Date(minDate);
      min.setHours(0, 0, 0, 0);
      controlDate.setHours(0, 0, 0, 0);
      if (controlDate < min) {
        return { minDate: { requiredDate: min, actualDate: controlDate } };
      }
      return null;
    }
  };

}
